import { Fragment, useEffect, useRef, useState } from "react"
import { translateFirebaseError } from "../helpers/translateFirebaseError"

interface ProcessModalProps {
   onProcess: () => Promise<void>
   isOpen: boolean
   onClose: () => void
}

export default function ProcessModal({ onProcess, isOpen, onClose }: ProcessModalProps) {
   const [error, setError] = useState<Error | null>(null)
   const [isLoading, setIsLoading] = useState(false)
   const [showSuccess, setShowSuccess] = useState(false)
   const isProcessingRef = useRef(false)

   useEffect(() => {
      if (isOpen && !isProcessingRef.current) {
         isProcessingRef.current = true
         setError(null)
         setIsLoading(true)
         onProcess()
            .then(() => {
               setIsLoading(false)
            })
            .catch((error) => {
               if (import.meta.env.VITE_ENVIRONMENT === "development")
                  console.error("Process error\n", error, "\n", JSON.stringify(error, null, 2))
               const finalError = translateFirebaseError(error)
               setError(finalError)
               setIsLoading(false)
            })
            .finally(() => {
               isProcessingRef.current = false
            })
      }
   }, [isOpen, onProcess])

   useEffect(() => {
      if (!isLoading && !error) {
         setShowSuccess(true)
         const timer = setTimeout(() => {
            setShowSuccess(false)
            onClose()
         }, 800)
         return () => clearTimeout(timer)
      }
   }, [isLoading, error, onClose])

   if (!isOpen) return null

   return (
      <div className={`modal ${isLoading || error || showSuccess ? "modal-open" : ""}`}>
         <div className="modal-box relative">
            {isLoading ? (
               <div className="flex flex-col items-center justify-center gap-4">
                  <div className="loading loading-spinner loading-lg"></div>
                  <p>Traitement en cours...</p>
               </div>
            ) : error ? (
               <div className="flex flex-col items-center justify-center gap-4">
                  <div className="alert alert-error">
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="stroke-current shrink-0 h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                     </svg>
                     {error.message.split(/\n/g).map((line, i) => (
                        <Fragment key={i}>
                           {line}
                           <br />
                        </Fragment>
                     ))}
                  </div>
                  <button className="btn btn-primary" onClick={onClose}>
                     Fermer
                  </button>
               </div>
            ) : showSuccess ? (
               <div className="flex flex-col items-center justify-center gap-4">
                  <div className="alert alert-success">
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="stroke-current shrink-0 h-16 w-16"
                        fill="none"
                        viewBox="0 0 24 24"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                     </svg>
                  </div>
               </div>
            ) : null}
         </div>
      </div>
   )
}
